export const NAV_ITEMS = [
  { path: '/', label: 'Home' },
  { path: '/live-auctions', label: 'Live Auctions' },
  { path: '/shop', label: 'Buy Now Shop' },
  { path: '/vault', label: 'The Vault' },
  { path: '/weekly-bundle', label: 'Weekly Bundle' },
  { path: '/consign-with-us', label: 'Consign With Us' },
  { path: '/drops-schedule', label: 'Drops & Schedule' },
  { path: '/support', label: 'Support' },
  { path: '/about', label: 'About' },
  { path: '/contact', label: 'Contact' },
]

export const INNER_PAGES = {
  '/live-auctions': {
    eyebrow: 'Real-time bidding',
    title: 'Live Auctions',
    description:
      'Track the room, watch the clock, and move fast on premium cards with a layout built for momentum, trust, and clear bidding decisions.',
    primaryCta: { label: 'View Upcoming Lots', href: '/drops-schedule' },
    secondaryCta: { label: 'Ask Auction Support', href: '/support' },
    metrics: [
      { value: '24/7', label: 'Bid monitoring' },
      { value: 'Fast', label: 'Lot updates' },
      { value: 'Secure', label: 'Payment flow' },
    ],
    cards: [
      {
        title: 'Auction room clarity',
        text: 'Large lot imagery, concise descriptions, and visible timing cues keep every decision readable.',
      },
      {
        title: 'High-stakes pacing',
        text: 'The layout is tuned for urgency without feeling crowded, so collectors can react quickly.',
      },
      {
        title: 'Trusted checkout',
        text: 'Winning bids move into a clean post-auction flow for confirmation and fulfillment.',
      },
    ],
    steps: [
      'Open the current lot and review the card details.',
      'Place a bid and track the countdown in real time.',
      'Confirm checkout and follow the fulfillment status.',
    ],
  },
  '/shop': {
    eyebrow: 'Ready to buy',
    title: 'Buy Now Shop',
    description:
      'A direct-purchase layout for collectors who want the card now, with filters, product focus, and a clean path to checkout.',
    primaryCta: { label: 'Browse Featured Inventory', href: '/live-auctions' },
    secondaryCta: { label: 'See Weekly Picks', href: '/weekly-bundle' },
    metrics: [
      { value: 'Instant', label: 'Purchase flow' },
      { value: 'Curated', label: 'Product drops' },
      { value: 'Visible', label: 'Inventory status' },
    ],
    cards: [
      {
        title: 'Featured inventory',
        text: 'Highlight standout cards up front so the best items never hide behind the noise.',
      },
      {
        title: 'Collector-focused filters',
        text: 'Quick sorting by set, player, grade, and price helps users get where they need to go.',
      },
      {
        title: 'Conversion-ready detail',
        text: 'Each product view should answer condition, value, and shipping questions in one screen.',
      },
    ],
    steps: [
      'Select the category or set you want.',
      'Inspect images, grading, and price details.',
      'Add to cart and check out without friction.',
    ],
  },
  '/vault': {
    eyebrow: 'Protected inventory',
    title: 'The Vault',
    description:
      'A premium holding space for rare inventory, high-value cards, and private collector access with a darker, more secure feel.',
    primaryCta: { label: 'Request Vault Access', href: '/contact' },
    secondaryCta: { label: 'Learn About Consignment', href: '/consign-with-us' },
    metrics: [
      { value: 'Private', label: 'Viewing access' },
      { value: 'Rare', label: 'Stored items' },
      { value: 'Verified', label: 'Condition records' },
    ],
    cards: [
      {
        title: 'Protected presentation',
        text: 'A vault page should feel controlled, premium, and hard to enter without intent.',
      },
      {
        title: 'Record keeping',
        text: 'Inventory history, grading notes, and owner context should stay easy to review.',
      },
      {
        title: 'Selective access',
        text: 'Only the right buyers should see the right items at the right time.',
      },
    ],
    steps: [
      'Submit a request for access or storage.',
      'Review inventory details and ownership notes.',
      'Coordinate private purchase or release.',
    ],
  },
  '/weekly-bundle': {
    eyebrow: 'Curated releases',
    title: 'Weekly Bundle',
    description:
      'A punchy product story for limited bundles, highlight cards, and recurring drops that feel special every week.',
    primaryCta: { label: 'See This Week', href: '/drops-schedule' },
    secondaryCta: { label: 'View Shop', href: '/shop' },
    metrics: [
      { value: 'Weekly', label: 'Drop cadence' },
      { value: 'Limited', label: 'Bundle count' },
      { value: 'Curated', label: 'Card mix' },
    ],
    cards: [
      {
        title: 'Drop anticipation',
        text: 'The page should build excitement before the bundle goes live.',
      },
      {
        title: 'Clear value story',
        text: 'Show why the bundle matters with a strong mix of cards and price appeal.',
      },
      {
        title: 'Fast action',
        text: 'Make it obvious when the bundle is live, sold out, or coming soon.',
      },
    ],
    steps: [
      'Check the current bundle lineup.',
      'Review the value and included cards.',
      'Buy before inventory sells out.',
    ],
  },
  '/consign-with-us': {
    eyebrow: 'Seller onboarding',
    title: 'Consign With Us',
    description:
      'A trust-first page for sellers who want a clear path from submission to sale, with premium treatment and simple communication.',
    primaryCta: { label: 'Start Consignment', href: '/contact' },
    secondaryCta: { label: 'Read Support', href: '/support' },
    metrics: [
      { value: 'Simple', label: 'Submission flow' },
      { value: 'Fair', label: 'Review process' },
      { value: 'Tracked', label: 'Status updates' },
    ],
    cards: [
      {
        title: 'Submission review',
        text: 'Explain what collectors should send and what happens after the intake request.',
      },
      {
        title: 'Seller confidence',
        text: 'Show commission expectations, quality standards, and communication timing.',
      },
      {
        title: 'Sale management',
        text: 'Keep the seller informed from approval to payout with no guessing.',
      },
    ],
    steps: [
      'Send the card details and photos.',
      'Receive review and approval feedback.',
      'Confirm sale setup and listing terms.',
    ],
  },
  '/drops-schedule': {
    eyebrow: 'Calendar view',
    title: 'Drops & Schedule',
    description:
      'A clean schedule page for upcoming drops, auction dates, and recurring weekly moments that collectors can plan around.',
    primaryCta: { label: 'Contact Support', href: '/support' },
    secondaryCta: { label: 'Browse Weekly Bundle', href: '/weekly-bundle' },
    metrics: [
      { value: 'Planned', label: 'Event dates' },
      { value: 'Live', label: 'Drop windows' },
      { value: 'Timed', label: 'Release rhythm' },
    ],
    cards: [
      {
        title: 'Upcoming events',
        text: 'A date-led layout makes the next drop obvious at a glance.',
      },
      {
        title: 'Release reminders',
        text: 'Collectors should know what is live now and what is coming next.',
      },
      {
        title: 'Timed urgency',
        text: 'Use time blocks and countdown cues to drive attendance and action.',
      },
    ],
    steps: [
      "Review this week's live events.",
      'Set your reminder for the next drop.',
      'Jump in when the window opens.',
    ],
  },
  '/support': {
    eyebrow: 'Help desk',
    title: 'Support',
    description:
      'A direct support page for orders, bids, shipping, and account questions with clear contact routes and helpful guidance.',
    primaryCta: { label: 'Email Support', href: 'mailto:cvinderise@yahoo.com' },
    secondaryCta: { label: 'Call Now', href: 'tel:6058655068' },
    metrics: [
      { value: 'Fast', label: 'Response focus' },
      { value: 'Clear', label: 'Resolution path' },
      { value: 'Direct', label: 'Contact options' },
    ],
    cards: [
      {
        title: 'Order help',
        text: 'Answer shipping, checkout, and payment questions without sending users in circles.',
      },
      {
        title: 'Bid support',
        text: 'Explain timing, auction rules, and post-win steps in plain language.',
      },
      {
        title: 'Account issues',
        text: 'Make contact details and follow-up expectations impossible to miss.',
      },
    ],
    steps: [
      'Choose the issue category.',
      'Send the details and order reference.',
      'Wait for a direct response from the team.',
    ],
  },
  '/about': {
    eyebrow: 'Brand story',
    title: 'About OBC',
    description:
      'A bold about page that explains the brand, the collector mindset, and why the marketplace feels more like an arena than a storefront.',
    primaryCta: { label: 'Contact the Team', href: '/contact' },
    secondaryCta: { label: 'Explore Auctions', href: '/live-auctions' },
    metrics: [
      { value: 'Collector', label: 'First mindset' },
      { value: 'Premium', label: 'Presentation style' },
      { value: 'Trusted', label: 'Transaction flow' },
    ],
    cards: [
      {
        title: 'What the brand feels like',
        text: 'High energy, premium presentation, and clear product focus from top to bottom.',
      },
      {
        title: 'What users need',
        text: 'Fast decisions, visible trust signals, and a layout that never gets in the way.',
      },
      {
        title: 'What matters most',
        text: 'The platform should make cards feel valuable and the buying path feel certain.',
      },
    ],
    steps: [
      'Review the marketplace structure.',
      'Explore the product categories.',
      'Reach out when you are ready to buy or sell.',
    ],
  },
  '/contact': {
    eyebrow: "Let's talk",
    title: 'Contact',
    description:
      'A simple contact page with the key ways to reach the team for sales, support, consignments, and general questions.',
    primaryCta: { label: 'Email Us', href: 'mailto:cvinderise@yahoo.com' },
    secondaryCta: { label: 'Call Us', href: 'tel:6058655068' },
    metrics: [
      { value: 'Email', label: 'cvinderise@yahoo.com' },
      { value: 'Phone', label: '605-865-5068' },
      { value: 'Address', label: 'Watford City, ND' },
    ],
    cards: [
      {
        title: 'Sales contact',
        text: 'Use this for shopping, product questions, and new drops.',
      },
      {
        title: 'Support contact',
        text: 'Use this for shipping, order status, and post-purchase help.',
      },
      {
        title: 'Consignment contact',
        text: 'Use this for intake, valuation, and approval requests.',
      },
    ],
    steps: [
      'Pick the reason for reaching out.',
      'Send the details and best contact method.',
      'Wait for a direct reply from the team.',
    ],
  },
  '/terms-and-conditions': {
    eyebrow: 'Policy page',
    title: 'Terms & Conditions',
    description:
      'A structured policy page that explains usage expectations, purchase rules, and the basic terms that govern the marketplace.',
    primaryCta: { label: 'Contact Support', href: '/support' },
    secondaryCta: { label: 'Privacy Policy', href: '/privacy-policy' },
    metrics: [
      { value: 'Rules', label: 'Marketplace use' },
      { value: 'Terms', label: 'Purchase flow' },
      { value: 'Clarity', label: 'User expectations' },
    ],
    cards: [
      {
        title: 'User obligations',
        text: 'State how visitors should use the platform and what conduct is expected.',
      },
      {
        title: 'Sale conditions',
        text: 'Explain order handling, cancellations, and the rules around live bids.',
      },
      {
        title: 'Limitation notes',
        text: 'Make the legal boundaries and support process easy to find.',
      },
    ],
  },
  '/return-refund-policy': {
    eyebrow: 'Policy page',
    title: 'Return & Refund Policy',
    description:
      'A clear returns page that explains eligibility, timing, and how customers can request help when something is wrong.',
    primaryCta: { label: 'Read Support', href: '/support' },
    secondaryCta: { label: 'Contact Us', href: '/contact' },
    metrics: [
      { value: 'Clear', label: 'Eligibility rules' },
      { value: 'Timed', label: 'Request window' },
      { value: 'Simple', label: 'Help process' },
    ],
    cards: [
      {
        title: 'Return eligibility',
        text: 'Describe which items qualify and which condition issues matter.',
      },
      {
        title: 'Refund timing',
        text: 'Explain when refunds are reviewed and how they are issued.',
      },
      {
        title: 'Support path',
        text: 'Show exactly where a customer should go when a problem appears.',
      },
    ],
  },
  '/privacy-policy': {
    eyebrow: 'Policy page',
    title: 'Privacy Policy',
    description:
      'A privacy page that explains what information is collected, why it is used, and how collectors can contact the team.',
    primaryCta: { label: 'Contact Us', href: '/contact' },
    secondaryCta: { label: 'Terms & Conditions', href: '/terms-and-conditions' },
    metrics: [
      { value: 'Data', label: 'Collection overview' },
      { value: 'Use', label: 'Purpose summary' },
      { value: 'Trust', label: 'Collector confidence' },
    ],
    cards: [
      {
        title: 'Information use',
        text: 'Explain what information is gathered and how it helps the service run.',
      },
      {
        title: 'Security focus',
        text: 'State the general approach to protecting account and order details.',
      },
      {
        title: 'Contact rights',
        text: 'Tell users how they can ask questions or request changes.',
      },
    ],
  },
}

export function getInnerPage(path) {
  return INNER_PAGES[path] || INNER_PAGES['/about']
}
